import {
  CheckCircleFilled,
  ClockCircleOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Popover, Spin, Typography } from 'antd';
import React from 'react';
import { formatDateTime, formatNumber } from 'pages/star-shared/utils';
import { CalendarData, CalendarDay, CalendarStatus } from '../types';
import {
  formatDay,
  formatDayMonth,
  formatLiveHours,
  formatMonth,
  formatPeriod,
  weekdayOf,
} from '../utils';
import styles from '../style.module.scss';

/**
 * Trạng thái hiện trên ô. Ngoài ba trạng thái của máy chủ, ngày "Còn thiếu" được hạ xuống
 * "không bắt buộc" khi tháng đã chốt bằng file tháng (OPTIONAL) hoặc ngày nằm trong số cộng
 * dồn cũ (LEGACY) — docs/nhap-du-lieu/DAC-TA-TRANG-THAI-THANG-2026-10-02.md.
 */
type CellStatus = CalendarStatus | 'OPTIONAL' | 'LEGACY';

// Các trạng thái phân biệt bằng biểu tượng + chữ + màu (không chỉ bằng màu).
const STATUS_META: Record<CellStatus, { label: string; icon: React.ReactNode; className: string }> =
  {
    IMPORTED: { label: 'Đã nhập', icon: <CheckCircleFilled />, className: styles.cellImported },
    MISSING: {
      label: 'Còn thiếu',
      icon: <ExclamationCircleFilled />,
      className: styles.cellMissing,
    },
    FUTURE: { label: 'Chưa tới', icon: <ClockCircleOutlined />, className: styles.cellFuture },
    OPTIONAL: {
      label: 'Không bắt buộc',
      icon: <MinusCircleOutlined />,
      className: styles.cellOptional,
    },
    LEGACY: { label: 'Số cộng dồn', icon: <MinusCircleOutlined />, className: styles.cellOptional },
  };

const metaOf = (status: CellStatus) => STATUS_META[status] || STATUS_META.FUTURE;

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className={styles.popoverRow}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const DayDetail = ({ day, cell }: { day: CalendarDay; cell: CellStatus }) => {
  const hasNumbers = day.creators > 0 || Number(day.diamonds) > 0;
  return (
    <div className={styles.popover}>
      <Row label="Trạng thái" value={metaOf(cell).label} />
      {day.status === 'FUTURE' && <div>Ngày này chưa tới theo giờ TikTok (UTC+0).</div>}
      {cell === 'MISSING' && <div>Chưa có file của ngày này.</div>}
      {cell === 'OPTIONAL' && (
        <div>Tháng đã chốt bằng file tháng: ngày này không bắt buộc nhập.</div>
      )}
      {cell === 'LEGACY' && (
        <div>Ngày này nằm trong số cộng dồn nhập trước đây: không bắt buộc nhập.</div>
      )}
      {(day.status === 'IMPORTED' || hasNumbers) && (
        <>
          <Row label="Creator" value={formatNumber(day.creators || 0)} />
          <Row label="Kim cương" value={formatNumber(day.diamonds || 0)} />
          <Row label="Giờ LIVE" value={formatLiveHours(day.liveSeconds)} />
        </>
      )}
      {day.status === 'IMPORTED' && (
        <>
          <Row label="Người nhập" value={day.importedByName || 'Không rõ'} />
          <Row label="Lúc nhập" value={formatDateTime(day.importedAt)} />
        </>
      )}
      {day.status !== 'FUTURE' && (
        <Row label="Dòng nhập tay" value={formatNumber(day.manualRows || 0)} />
      )}
    </div>
  );
};

interface Props {
  data: CalendarData | null;
  loading: boolean;
}

/**
 * Dải lịch của một tháng (GET /data-import/calendar): mỗi ngày TikTok một ô, rê chuột /
 * chạm vào ô để xem số Creator, tổng KC, ai nhập, lúc nào, số dòng nhập tay.
 */
const MonthCalendar = ({ data, loading }: Props) => {
  if (!data) {
    return (
      <div className={styles.centered}>
        {loading ? (
          <Spin />
        ) : (
          <Typography.Text type="secondary">Chưa tải được lịch nhập.</Typography.Text>
        )}
      </div>
    );
  }

  const days = Array.isArray(data.days) ? data.days : [];
  const legacyRanges = Array.isArray(data.legacyRanges) ? data.legacyRanges : [];
  const closed = data.status?.status === 'MONTH_FILE' || !!data.monthFile;
  const cellOf = (day: CalendarDay): CellStatus => {
    if (day.status !== 'MISSING') return day.status;
    if (closed) return 'OPTIONAL';
    const date = day.date.slice(0, 10);
    const inLegacy = legacyRanges.some(
      (range) => date >= range.periodStart.slice(0, 10) && date <= range.periodEnd.slice(0, 10)
    );
    return inLegacy ? 'LEGACY' : 'MISSING';
  };
  const imported = days.filter((day) => day.status === 'IMPORTED');
  const missing = days.filter((day) => cellOf(day) === 'MISSING');
  const covered = days.filter((day) => cellOf(day) === 'LEGACY');
  const due = imported.length + missing.length + covered.length;
  const importedDiamonds = imported.reduce((sum, day) => sum + (Number(day.diamonds) || 0), 0);

  return (
    <Spin spinning={loading}>
      <p className={styles.summary}>
        {closed ? (
          `Tháng đã chốt bằng file tháng; có ${imported.length} ngày chi tiết theo file ngày.`
        ) : due === 0 ? (
          'Chưa có ngày nào của tháng này tới theo giờ TikTok (UTC+0).'
        ) : (
          <>
            Đã nhập {imported.length}/{due} ngày
            {covered.length > 0 ? ` (thêm ${covered.length} ngày có số cộng dồn cũ)` : ''}.{' '}
            {missing.length > 0 ? (
              <span className={styles.summaryMissing}>
                Còn thiếu: {missing.map((day) => day.date.slice(8, 10)).join(', ')}
              </span>
            ) : (
              'Không thiếu ngày nào.'
            )}
          </>
        )}
      </p>
      <p className={styles.note}>
        Ngày tính theo giờ TikTok (UTC+0), hôm nay là {formatDay(data.today)}.
        {imported.length > 0 &&
          ` Tổng Kim cương các ngày đã nhập: ${formatNumber(importedDiamonds)}.`}
      </p>

      <div className={styles.legend}>
        <span className={`${styles.legendItem} ${styles.legendImported}`}>
          <CheckCircleFilled /> Đã nhập
        </span>
        <span className={`${styles.legendItem} ${styles.legendMissing}`}>
          <ExclamationCircleFilled /> Còn thiếu
        </span>
        <span className={styles.legendItem}>
          <ClockCircleOutlined /> Chưa tới
        </span>
        {(closed || covered.length > 0) && (
          <span className={styles.legendItem}>
            <MinusCircleOutlined /> Không bắt buộc
          </span>
        )}
        <span className={styles.legendItem}>
          <EditOutlined /> Có dòng nhập tay
        </span>
      </div>

      <ul className={styles.calendar}>
        {days.map((day) => {
          const cell = cellOf(day);
          const meta = metaOf(cell);
          const isToday = day.date === data.today;
          const weekday = weekdayOf(day.date);
          return (
            <li key={day.date}>
              <Popover
                trigger={['hover', 'focus']}
                // Lề trong suốt hai bên: ô ở cột ngoài cùng thì hộp vẫn cách mép màn hình 8px.
                styles={{ root: { paddingInline: 8 } }}
                title={`Ngày ${formatDay(day.date)}${weekday ? ` (${weekday})` : ''}`}
                content={<DayDetail day={day} cell={cell} />}
              >
                <button
                  type="button"
                  className={`${styles.cell} ${meta.className} ${isToday ? styles.cellToday : ''}`}
                  aria-label={`Ngày ${formatDay(day.date)}: ${meta.label}`}
                >
                  <span className={styles.day}>
                    <span className={styles.dayNumber}>{day.date.slice(8, 10)}</span>
                    <span className={styles.weekday}>{isToday ? 'Hôm nay' : weekday}</span>
                  </span>
                  <span className={styles.status}>
                    {meta.icon} {meta.label}
                  </span>
                  {day.status === 'IMPORTED' && (
                    <span className={styles.meta}>{formatNumber(day.diamonds || 0)} KC</span>
                  )}
                  {day.manualRows > 0 && (
                    <span className={styles.meta}>
                      <EditOutlined /> {formatNumber(day.manualRows)} nhập tay
                    </span>
                  )}
                </button>
              </Popover>
            </li>
          );
        })}
      </ul>

      {data.monthFile && (
        <p className={styles.infoLine}>
          <InfoCircleOutlined />
          <span>
            Tháng này đã có file tháng{' '}
            {formatPeriod(data.monthFile.periodStart, data.monthFile.periodEnd)}
            {data.monthFile.importedAt
              ? `, nhập lúc ${formatDateTime(data.monthFile.importedAt)}`
              : ''}
            {data.monthFile.importedByName ? ` bởi ${data.monthFile.importedByName}` : ''}: BXH dùng
            số của file tháng; file ngày chỉ dùng cho Điểm Sao và phân tích.
          </span>
        </p>
      )}
      {legacyRanges.map((range) => (
        <p className={styles.infoLine} key={`${range.periodStart}-${range.periodEnd}`}>
          <InfoCircleOutlined />
          <span>
            Tháng này có số cộng dồn {formatDayMonth(range.periodStart)} →{' '}
            {formatDayMonth(range.periodEnd)} nhập trước đây ({formatNumber(range.creators)}{' '}
            Creator, {formatNumber(range.diamonds)} KC): BXH dùng số này; file ngày{' '}
            {range.periodStart.slice(8, 10)}–{range.periodEnd.slice(8, 10)} chỉ dùng cho Điểm Sao và
            phân tích.
          </span>
        </p>
      ))}
      {days.length === 0 && (
        <Typography.Text type="secondary">
          Không có dữ liệu lịch của tháng {formatMonth(data.month)}.
        </Typography.Text>
      )}
    </Spin>
  );
};

export default MonthCalendar;
